import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import SectionsList from './SectionsList'
import { pluralize } from '../../../utils/textHelpers'

const selectionLabel = (sectionsCount, questionsCount) => {
  if (questionsCount === 0)
    return 'Aucune section ni question sélectionnée'

  return `${pluralize(sectionsCount, 'section')} & ${pluralize(questionsCount, 'question')} sélectionnées`
}

const CustomExportForm = ({
  exportUrl,
  onSubmit = null,
  sections,
  selectedQuestions,
  onQuestionChange,
  title = 'Sélectionnez les sections et questions à exporter',
  formId = 'export-form'
}) => {
  // Calculate totals
  const getTotalSelectedQuestions = useMemo(
    () => Object.values(selectedQuestions).reduce(
      (total, questions) => total + questions.length, 0
    ), [selectedQuestions]
  )

  const getTotalSelectedSections = useMemo(() => Object.values(selectedQuestions).filter(
    questions => questions.length > 0
  ).length, [selectedQuestions])

  const getSectionSelectedCount = useCallback(
    sectionId => (selectedQuestions[sectionId] || []).length, [selectedQuestions]
  )

  // Handle selection changes
  const handleQuestionSelect = useCallback((sectionId, questionId) => {
    const currentQuestions = selectedQuestions[sectionId] || []
    const isSelected = currentQuestions.includes(questionId)

    /* eslint-disable multiline-ternary */
    const newQuestions = isSelected
      ? currentQuestions.filter(id => id !== questionId)
      : [...currentQuestions, questionId]
    /* eslint-enable multiline-ternary */

    onQuestionChange({
      ...selectedQuestions,
      [sectionId]: newQuestions
    })
  }, [selectedQuestions, onQuestionChange])

  const handleSectionSelect = useCallback(sectionId => {
    const section = sections.find(sect => sect.id === sectionId)
    if (!section)
      return


    const currentQuestions = selectedQuestions[sectionId] || []
    const allQuestionIds = section.questions.map(question => question.id)

    // Toggle: if all selected, deselect all; otherwise select all
    const newQuestions = currentQuestions.length === allQuestionIds.length? []: allQuestionIds

    onQuestionChange({
      ...selectedQuestions,
      [sectionId]: newQuestions
    })
  }, [sections, selectedQuestions, onQuestionChange])

  const handleSelectAllQuestions = useCallback(sectionId => {
    const section = sections.find(sect => sect.id === sectionId)
    if (!section)
      return


    onQuestionChange({
      ...selectedQuestions,
      [sectionId]: section.questions.map(question => question.id)
    })
  }, [sections, selectedQuestions, onQuestionChange])

  const handleDeselectAllQuestions = useCallback(sectionId => {
    onQuestionChange({
      ...selectedQuestions,
      [sectionId]: []
    })
  }, [selectedQuestions, onQuestionChange])

  const handleSelectAllSections = useCallback(() => {
    const newSelection = {}
    sections.forEach(section => {
      newSelection[section.id] = section.questions.map(question => question.id)
    })
    onQuestionChange(newSelection)
  }, [sections, onQuestionChange])

  const handleDeselectAllSections = useCallback(() => {
    const newSelection = {}
    sections.forEach(section => {
      newSelection[section.id] = []
    })
    onQuestionChange(newSelection)
  }, [sections, onQuestionChange])

  return (
    <form
      id={formId}
      action={exportUrl}
      method="POST"
      onSubmit={onSubmit}
    >
      <input
        type="hidden"
        name="authenticity_token"
        value={document.querySelector('meta[name="csrf-token"]')?.content}
      />
      <div className="form-group">
        <h5 className="font-weight-bold">
          {title}
          {' '}
          <span className="text-danger ml-1">*</span>
        </h5>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center">
            <strong>Sections :</strong>
            <div className="d-flex align-items-center ml-1">
              <button
                type="button"
                className="btn btn-link p-0"
                onClick={handleSelectAllSections}
              >
                Tout cocher
              </button>
              <span className="mx-1">/</span>
              <button
                type="button"
                className="btn btn-link p-0"
                onClick={handleDeselectAllSections}
              >
                Tout désélectionner
              </button>
            </div>
          </div>
          <strong>{selectionLabel(getTotalSelectedSections, getTotalSelectedQuestions)}</strong>
        </div>
        <SectionsList
          sections={sections}
          selectedQuestions={selectedQuestions}
          getSectionSelectedCount={getSectionSelectedCount}
          onSectionSelect={handleSectionSelect}
          onQuestionSelect={handleQuestionSelect}
          onSelectAllQuestions={handleSelectAllQuestions}
          onDeselectAllQuestions={handleDeselectAllQuestions}
        />
      </div>
    </form>
  )
}

CustomExportForm.propTypes = {
  exportUrl: PropTypes.string.isRequired,
  onQuestionChange: PropTypes.func.isRequired,
  sections: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    questions: PropTypes.arrayOf(PropTypes.shape({
      context: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
      title: PropTypes.string
    }))
  })).isRequired,
  selectedQuestions: PropTypes.objectOf(
    PropTypes.arrayOf(PropTypes.number)
  ).isRequired
}

export default CustomExportForm